import { Routes, Route } from "react-router-dom";
import HomePage from "../pages/HomePage";
import NotFoundPage from "../pages/NotFoundPage";
import UnauthorizedPage from "../pages/UnauthorizedPage";
import MainLayout from "../layouts/MainLayout";
import ReturnPolicyPage from "../pages/ReturnAndExchange";
import ContactUsPage from "../pages/ContactUs";
import AboutUsPage from "../pages/AboutUs";
import Terms from "../pages/Terms";
import ProductsPage from "../pages/ProductsPage";
import SignIn from "../pages/SignIn";
import SignUp from "../pages/SignUp";
import AuthLayout from "../layouts/AuthLayout";
import MyProfile from "../pages/MyProfile";
import AddressManagement from "../pages/Address";
import ProtectedRoute from "./ProtectedRoute";
import MyOrders from "../pages/MyOrders";
import FavoritesManagement from "../pages/Favourites";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import CartPage from "../pages/CartPage";
import ProductDetailsPage from "../pages/ProductDetailsPage";
import CheckoutPage from "../pages/CheckoutPage";
import CategoriesManagementPage from "../pages/NonCustomer/CategoriesManagementPage";
import ProductsManagementPage from "../pages/NonCustomer/ProductsManagementPage";
import UsersManagementPage from "../pages/NonCustomer/UsersManagement";
import OrdersManagementPage from "../pages/NonCustomer/OrdersManagementPage";
import RolesManagementPage from "../pages/NonCustomer/RolesManagementPage";
import ReportManagementPage from "../pages/NonCustomer/ReportManagementPage";
import InventoryManagementPage from "../pages/NonCustomer/InventoryManagementPage";
import { useAuth } from "../context/AuthContext";
import DashboardPage from "../pages/NonCustomer/Dashboard";
import PurchaseOrderManagementPage from "../pages/NonCustomer/PurchaseOrderManagementPage";
import PurchaseReturnManagementPage from "../pages/NonCustomer/PurchaseReturnManagementPage";
import POSPage from "../pages/NonCustomer/PointOfSaleManagementPage";
import DeliveryPage from "../pages/NonCustomer/DeliveryManagementPage";
import POSHistory from "../pages/NonCustomer/PointOfSaleHistory";
import CompanySettingsPage from "../pages/NonCustomer/CompanySettingsPage";
import InvoiceManagementPage from "../pages/NonCustomer/InvoiceManagementPage";
import CouponsPage from "../pages/NonCustomer/CouponManagementPage";
import PermissionRoute from "../hooks/PermissionRoute";
import { PERMISSIONS } from "../utils/permissions";
import GiftManagement from "../pages/NonCustomer/GiftManagement";
import PaymentCallback from "../components/PaymentCallback";
import ExpensesManagementPage from "../pages/NonCustomer/ExpensesManagementPage";
import RateManagementPage from "../pages/NonCustomer/RatingManagementPage";
import MyRatingsPage from "../pages/MyRatingsPage";
import SubCategoryPage from "../pages/SubCategoryPage";
import InvoiceVerificationPage from "../pages/InvoiceVerificationPage";
import MyReturns from "../pages/MyReturns";
import ReturnRequestDetails from "../pages/ReturnRequestDetails";
import ReturnsManagementPage from "../pages/NonCustomer/ReturnsManagementPage";

const AppRoutes = () => {
  const { isAuthenticated, permissions, user } = useAuth();

  // Check if user has wildcard or any admin permissions
  const hasWildcard =
    permissions.includes("*") ||
    permissions.includes("all") ||
    permissions.includes("ALL");

  const hasAdminAccess =
    hasWildcard ||
    permissions.some((p) =>
      [
        PERMISSIONS.VIEW_DASHBOARD,
        PERMISSIONS.VIEW_USERS,
        PERMISSIONS.VIEW_ROLES,
        PERMISSIONS.VIEW_PRODUCTS,
        PERMISSIONS.VIEW_CATEGORIES,
        PERMISSIONS.VIEW_ORDERS,
        PERMISSIONS.VIEW_REPORTS,
      ].includes(p as any),
    );

  // console.log('[ROUTES] Auth status:', {
  //   isAuthenticated,
  //   permissions,
  //   hasWildcard,
  //   hasAdminAccess
  // });

  return (
    <Routes>
      <Route
        path="/invoice/:invoiceId/genuine"
        element={<InvoiceVerificationPage />}
      />

      {/* Layout with header and footer */}
      <Route element={<MainLayout />}>
        {/* Home/Dashboard Route */}
        <Route
          path="/"
          element={
            isAuthenticated && hasAdminAccess && user?.role !== "customer" ? (
              <DashboardPage />
            ) : (
              <HomePage />
            )
          }
        />

        {/* Payment callback routes */}
        <Route path="/payment/success" element={<PaymentCallback />} />
        <Route path="/payment/success/:orderId" element={<PaymentCallback />} />
        <Route path="/payment/error" element={<PaymentCallback />} />
        <Route path="/payment/error/:orderId" element={<PaymentCallback />} />
        <Route path="/payment/callback" element={<PaymentCallback />} />
        <Route
          path="/payment/callback/:orderId"
          element={<PaymentCallback />}
        />
        <Route path="/home" element={<HomePage />} />

        {/* Public Routes */}
        <Route path="/category/:categoryId" element={<SubCategoryPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/product/:productId" element={<ProductDetailsPage />} />
        <Route path="/privacy-policy" element={<ReturnPolicyPage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/about" element={<AboutUsPage />} />

        {/* Protected Routes - Require Authentication Only (Customer Routes) */}
        <Route
          path="/account/profile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/orders"
          element={
            <ProtectedRoute>
              <MyOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/address"
          element={
            <ProtectedRoute>
              <AddressManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/favorites"
          element={
            <ProtectedRoute>
              <FavoritesManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/my-ratings"
          element={
            <ProtectedRoute>
              <MyRatingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/returns"
          element={
            <ProtectedRoute>
              <MyReturns />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/returns/:id"
          element={
            <ProtectedRoute>
              <ReturnRequestDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/cart"
          element={
            <ProtectedRoute>
              <CartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes - Require Authentication + Specific Permissions */}

        {/* Dashboard - Add explicit route for admin dashboard */}
        <Route
          path="/admin/dashboard"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_DASHBOARD]}>
              <DashboardPage />
            </PermissionRoute>
          }
        />

        {/* Categories Management */}
        <Route
          path="/admin/categories"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_CATEGORIES]}
            >
              <CategoriesManagementPage />
            </PermissionRoute>
          }
        />

        {/* Products Management */}
        <Route
          path="/admin/products"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_PRODUCTS]}>
              <ProductsManagementPage />
            </PermissionRoute>
          }
        />

        {/* Company Settings */}
        <Route
          path="/admin/company-settings"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_SETTINGS]}>
              <CompanySettingsPage />
            </PermissionRoute>
          }
        />

        {/* Users Management */}
        <Route
          path="/admin/users"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_USERS]}>
              <UsersManagementPage />
            </PermissionRoute>
          }
        />

        {/* Orders Management */}
        <Route
          path="/admin/orders"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_ORDERS]}>
              <OrdersManagementPage />
            </PermissionRoute>
          }
        />

        {/* Orders Management */}
        <Route
          path="/admin/rating"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_RATING]}>
              <RateManagementPage />
            </PermissionRoute>
          }
        />

        {/* Invoices Management */}
        <Route
          path="/admin/invoices"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_INVOICES]}>
              <InvoiceManagementPage />
            </PermissionRoute>
          }
        />

        {/* Inventory Management */}
        <Route
          path="/admin/inventory"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_INVENTORY]}>
              <InventoryManagementPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/admin/gifts"
          element={
            <ProtectedRoute>
              <GiftManagement />
            </ProtectedRoute>
          }
        />
        {/* Purchase Orders */}
        <Route
          path="/admin/purchase-orders"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_PURCHASE_ORDERS]}
            >
              <PurchaseOrderManagementPage />
            </PermissionRoute>
          }
        />

        {/* Purchase Returns */}
        <Route
          path="/admin/purchase-returns"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_PURCHASE_RETURNS]}
            >
              <PurchaseReturnManagementPage />
            </PermissionRoute>
          }
        />

        {/* Point of Sale */}
        <Route
          path="/admin/pos"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.ACCESS_POS]}>
              <POSPage />
            </PermissionRoute>
          }
        />

        {/* POS History */}
        <Route
          path="/admin/pos/history"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_POS_HISTORY]}
            >
              <POSHistory />
            </PermissionRoute>
          }
        />

        {/* Employee Expenses */}
        <Route
          path="/admin/employee-expenses"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_EMPLOYEE_EXPENSES]}
            >
              <ExpensesManagementPage />
            </PermissionRoute>
          }
        />

        {/* Coupons Management */}
        <Route
          path="/admin/coupon"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_COUPONS]}>
              <CouponsPage />
            </PermissionRoute>
          }
        />

        {/* Returns & Exchanges Management */}
        <Route
          path="/admin/returns"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_RETURNS]}>
              <ReturnsManagementPage />
            </PermissionRoute>
          }
        />

        {/* Deliveries Management */}
        <Route
          path="/admin/deliveries"
          element={
            <PermissionRoute
              requiredPermissions={[PERMISSIONS.VIEW_DELIVERIES]}
            >
              <DeliveryPage />
            </PermissionRoute>
          }
        />

        {/* Reports */}
        <Route
          path="/admin/reports"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_REPORTS]}>
              <ReportManagementPage />
            </PermissionRoute>
          }
        />

        {/* Roles Management */}
        <Route
          path="/admin/roles"
          element={
            <PermissionRoute requiredPermissions={[PERMISSIONS.VIEW_ROLES]}>
              <RolesManagementPage />
            </PermissionRoute>
          }
        />

        {/* Unauthorized Page */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
      </Route>

      {/* Layout without header/footer */}
      <Route element={<AuthLayout />}>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
